import React from 'react';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { TutorBookingScheduleLabels } from '../../utils/tutorBookingChangeProposal';

export type { TutorBookingScheduleLabels };

export interface TutorBookingScheduleBlockProps {
  schedule: TutorBookingScheduleLabels;
  emphasize?: boolean;
  /** Struck-through previous time shown before the new time (pending reschedule). */
  previousTimeLabel?: string;
  statusChip?: React.ReactNode;
  /** Where to place the status chip. Default keeps it beside the time. */
  statusChipPlacement?: 'timeRow' | 'dateRow';
  /** Optional actions rendered inside the schedule box (e.g. accept / decline). */
  actions?: React.ReactNode;
  /** When set, hover (and touch) reveals a full-area link that navigates to this path. */
  viewInCalendarTo?: string | null;
  viewInCalendarLabel?: string;
}

/** Card-style date/time row used on tutor booking list cards and detail change section. */
export default function TutorBookingScheduleBlock({
  schedule,
  emphasize = false,
  previousTimeLabel,
  statusChip,
  statusChipPlacement = 'timeRow',
  actions,
  viewInCalendarTo,
  viewInCalendarLabel = 'View in Calendar',
}: TutorBookingScheduleBlockProps) {
  const chipOnDateRow = statusChipPlacement === 'dateRow' ? statusChip : null;
  const chipOnTimeRow = statusChipPlacement === 'timeRow' ? statusChip : null;
  const calendarTo = viewInCalendarTo?.trim() || null;

  return (
    <Stack
      spacing={actions ? 1.25 : 0}
      sx={(theme) => ({
        position: 'relative',
        py: 1.5,
        px: 1.5,
        borderRadius: 1.5,
        bgcolor: 'action.hover',
        border: '1px solid',
        borderColor: emphasize ? 'warning.main' : 'divider',
        ...(calendarTo
          ? {
              '&:hover .schedule-calendar-cta, &:focus-within .schedule-calendar-cta': {
                opacity: 1,
                pointerEvents: 'auto',
              },
              '@media (hover: none)': {
                '& .schedule-calendar-cta': {
                  opacity: 1,
                  pointerEvents: 'auto',
                },
              },
              '&:hover': {
                borderColor: alpha(theme.palette.primary.main, 0.55),
              },
            }
          : null),
      })}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <CalendarMonthOutlinedIcon
          sx={{
            fontSize: 18,
            color: emphasize ? 'warning.main' : 'text.secondary',
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            sx={{ minWidth: 0 }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.35 }}>
              {schedule.dateLabel}
            </Typography>
            {chipOnDateRow}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 0.25 }}
          >
            {previousTimeLabel ? (
              <>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ textDecoration: 'line-through' }}
                >
                  {previousTimeLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary" aria-hidden>
                  →
                </Typography>
                <Typography variant="caption" color="text.primary" fontWeight={600}>
                  {schedule.timeLabel}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {schedule.timeLabel}
              </Typography>
            )}
            {chipOnTimeRow}
          </Stack>
        </Box>
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ flexShrink: 0, alignSelf: 'center' }}
        >
          <AccessTimeOutlinedIcon sx={{ fontSize: 18, color: 'text.primary' }} />
          <Typography variant="body2" color="text.primary" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {schedule.durationLabel}
          </Typography>
        </Stack>
      </Stack>
      {actions}
      {calendarTo ? (
        <Box
          className="schedule-calendar-cta"
          component={RouterLink}
          to={calendarTo}
          aria-label={viewInCalendarLabel}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
          onKeyDown={(event: React.KeyboardEvent) => event.stopPropagation()}
          sx={(theme) => ({
            position: 'absolute',
            inset: 0,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            textDecoration: 'none',
            color: 'text.secondary',
            bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.82 : 0.9),
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.15s ease, color 0.15s ease',
            px: 1.5,
            cursor: 'pointer',
            '&:hover': {
              color: 'primary.main',
            },
          })}
        >
          <CalendarMonthOutlinedIcon sx={{ fontSize: 16 }} />
          <Typography
            variant="button"
            sx={{
              textTransform: 'uppercase',
              fontWeight: 500,
              letterSpacing: '0.04em',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}
          >
            {viewInCalendarLabel}
          </Typography>
        </Box>
      ) : null}
    </Stack>
  );
}
